import { type SupportedLocale } from "../i18n-locales";
import { openingHoursSpecification } from "./business-hours";
import { localizedPath, type RouteKey } from "./routes";
import { absoluteUrl, ORG, SITE_NAME, SITE_URL } from "./seo";

type JsonLd = Record<string, unknown>;

// Organization schema for Enkey Agency. Reused on the homepage so search
// engines and AI assistants understand the business, address and founder.
export function organizationJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: ORG.name,
    legalName: ORG.legalName,
    url: SITE_URL,
    logo: ORG.logo,
    email: ORG.email,
    telephone: ORG.telephone,
    vatID: ORG.vatId,
    duns: ORG.duns,
    identifier: {
      "@type": "PropertyValue",
      propertyID: "REA",
      value: ORG.rea,
    },
    // Do not turn the founder's experience since 1999 into an unverified
    // incorporation date. Add foundingDate only when the entity's date is confirmed.
    founder: { "@id": `${SITE_URL}/#founder`, "@type": "Person", name: ORG.founder },
    address: {
      "@type": "PostalAddress",
      streetAddress: ORG.street,
      postalCode: ORG.postalCode,
      addressLocality: ORG.city,
      addressRegion: ORG.region,
      addressCountry: ORG.country,
    },
  };
}

export function founderJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE_URL}/#founder`,
    name: ORG.founder,
    jobTitle: "Founder",
    worksFor: { "@id": `${SITE_URL}/#organization` },
    url: SITE_URL,
  };
}

// WebSite schema (helps with sitelinks / site name).
export function webSiteJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: ["it", "en", "es", "fr", "de"],
  };
}

// ProfessionalService schema so the agency appears as a local business with
// its services and area served (AEO friendly).
export function professionalServiceJsonLd(services: string[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#service`,
    name: ORG.name,
    url: SITE_URL,
    image: ORG.logo,
    email: ORG.email,
    telephone: ORG.telephone,
    legalName: ORG.legalName,
    vatID: ORG.vatId,
    duns: ORG.duns,
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: ORG.street,
      postalCode: ORG.postalCode,
      addressLocality: ORG.city,
      addressRegion: ORG.region,
      addressCountry: ORG.country,
    },
    areaServed: { "@type": "Country", name: "Italia" },
    openingHoursSpecification: openingHoursSpecification(),
    makesOffer: services.map((name) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name },
    })),
  };
}

// FAQPage schema. Search engines can show these as rich results and AI
// assistants use them to answer questions directly (AEO friendly).
export function faqPageJsonLd(
  items: { question: string; answer: string }[],
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

// ItemList schema for editorial/portfolio listings. Only include items that are
// really shown on the page (no fabricated entries). `url` should be absolute.
export function itemListJsonLd(
  name: string,
  items: { name: string; url: string }[],
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: item.url,
    })),
  };
}

export type Crumb = { name: string; routeKey: RouteKey };

// BreadcrumbList schema built from a list of route keys in the current locale.
export function breadcrumbJsonLd(
  crumbs: { name: string; path: string }[],
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

// Convenience: build breadcrumb entries from route keys + labels.
export function crumbsFromKeys(
  entries: { name: string; routeKey: RouteKey }[],
  locale: SupportedLocale,
): { name: string; path: string }[] {
  return entries.map((e) => ({
    name: e.name,
    path: localizedPath(e.routeKey, locale),
  }));
}
